import React, { useState } from 'react';
import { deleteInsuranceCompanies } from '../../services/api/InsuranceService';
import {
  showSuccessDialog,
  useErrorDialog
} from '../../providers/ErrorDialogProvider';
import { groupCompaniesByType } from '../../services/api/InsuranceService';
import { getInsuranceTypeName } from '../../utils/insuranceTypeUtils';

const AgencyInsuranceTab = ({
  onEdit,
  onReturn,
  insuranceCompanies,
  onDataUpdated,
  onTabChange, // タブ切り替え用のコールバック
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { showError } = useErrorDialog();

  // チェックボックスの選択状態を管理
  const handleCheckboxChange = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  // 削除ボタンクリック
  const handleDeleteClick = () => {
    if (selectedCompanies.length === 0) {
      showError({
        message: '少なくとも1つの保険会社を選択してください。',
        error: null,
        statusCode: 422
      });
      return;
    }
    setShowConfirm(true);
  };

  // 削除確認
  const confirmDelete = async () => {
    setIsLoading(true);
    setErrorMessage(''); // エラーメッセージをリセット

    try {
      // URL末尾からagencyIdを取得
      const pathSegments = window.location.pathname.split('/');
      const agencyId = pathSegments[pathSegments.length - 1];

      // 削除処理を実行
      const result = await deleteInsuranceCompanies(selectedCompanies, agencyId);

      // 削除が成功したかをレスポンスで確認
      if (result && result.success !== false) {
        // 削除成功メッセージ
        showSuccessDialog(`${selectedCompanies.length}件の保険会社を削除しました。`);

        // 選択状態をリセット
        setSelectedCompanies([]);

        // モーダルを閉じる
        setShowConfirm(false);

        // データを更新（画面リフレッシュ）
        if (onDataUpdated) {
          await onDataUpdated();
        }

      } else {
        throw new Error('削除処理が失敗しました');
      }

    } catch (error) {
      console.error('削除エラー:', error);
      setErrorMessage('削除処理中にエラーが発生しました。');
      // エラー時はモーダルを閉じない
    } finally {
      setIsLoading(false);
    }
  };

  // 削除キャンセル
  const cancelDelete = () => {
    setShowConfirm(false);
    setErrorMessage(''); // エラーメッセージをリセット
  };

  // 選択された保険会社の詳細情報を取得
  const getSelectedCompanyDetails = (type) => {
    if (!selectedCompanies || !insuranceCompanies) return [];

    return insuranceCompanies.filter(company =>
      selectedCompanies.includes(company.id) && (!type || company.insurance_type === type)
    );
  };

  const getSelectedCompanyCount = (type) => {
    return insuranceCompanies.filter(company =>
      selectedCompanies.includes(company.id) && company.insurance_type === type
    ).length;
  };

  const group = groupCompaniesByType(insuranceCompanies);

  return (
    <>
      <div className="insurance-list c-max-middle">
        {insuranceCompanies.length > 0 ? (Object.entries(group).map(([type, companies]) => (
          <div key={type}>
            <h2 className="insurance-list-title">登録{getInsuranceTypeName(type)}会社名</h2>
            {companies.map((company, index) => (
              <div key={company.id || index} style={{ marginBottom: '8px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedCompanies.includes(company.id)}
                    onChange={() => handleCheckboxChange(company.id)}
                    disabled={isLoading}
                  />
                  {' '}
                  {company.name}
                </label>
              </div>
            ))}
          </div>))) : (
          <div>
            <h2 className="insurance-list-title">登録保険会社名</h2>
            <p>登録された保険会社がありません</p>
          </div>
        )}
      </div>

      <div className="action-buttons c-max-mini c-m-center">
        <button
          type="button"
          className="button button-secondary button-medium"
          onClick={onReturn}
          disabled={isLoading}
        >
          戻る
        </button>
        <button
          className="btn btn-primary button-medium"
          onClick={onEdit}
          disabled={isLoading}
        >
          編集する
        </button>
        <button
          className="btn btn-primary button-medium"
          onClick={handleDeleteClick}
          disabled={selectedCompanies.length === 0 || isLoading}
          style={{ backgroundColor: selectedCompanies.length === 0 ? '#ccc' : '#dc3545' }}
        >
          {isLoading ? '処理中...' : '削除する'}
        </button>
      </div>

      {/* エラーメッセージ表示 */}
      {errorMessage && (
        <div className="error-message" style={{
          color: 'red',
          textAlign: 'center',
          marginTop: '10px'
        }}>
          {errorMessage}
        </div>
      )}

      {/* 削除確認ダイアログ */}
      {showConfirm && (
        <div className="confirmation-popup-overlay">
          <div className="confirmation-popup-container">
            <div className="confirmation-popup-content">
              <h2 className="confirmation-title">保険会社の登録解除確認</h2>
              <div className="confirmation-details-02">
                <div className="confirmation-files">
                  {selectedCompanies.length > 0 ? (
                    <div>
                      {Object.keys(group).map(strType => {
                        const type = Number(strType);
                        const count = getSelectedCompanyCount(type);
                        const details = getSelectedCompanyDetails(type);
                        return (
                          <div key={type}>
                            <div className="confirmation-file-item font-large">
                              <div className="file-path">削除対象の{getInsuranceTypeName(type)}会社 ({count}社)</div>
                            </div>
                            <div className="selected-insurance-list" style={{ marginTop: '10px', marginLeft: '20px' }}>
                              {details.map((company) => (
                                <div key={company.id} className="confirmation-file-item font-medium">
                                  <div className="file-path">・{company.name}</div>
                                </div>
                              ))}
                            </div>

                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div>
                      <div className="confirmation-file-item font-large">
                        <div className="file-path">削除対象の保険会社 ({selectedCompanies.length}社)</div>
                      </div>
                      <div className="confirmation-file-item font-medium" style={{ marginLeft: '20px' }}>
                        <div className="file-path">削除対象の保険会社が見つかりません</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="confirmation-comment">
                  <div className="comment-header-error">注意事項</div>
                  <div className="comment-content comment-content--error">
                    選択した保険会社の登録を解除します。この操作を実行してよろしいですか？
                  </div>
                </div>

                {/* エラーメッセージをモーダル内にも表示 */}
                {errorMessage && (
                  <div className="error-message" style={{
                    color: 'red',
                    textAlign: 'center',
                    marginTop: '10px'
                  }}>
                    {errorMessage}
                  </div>
                )}
              </div>

              <div className="action-buttons c-max-mini c-m-center">
                <button
                  className="button button-secondary button-medium"
                  onClick={cancelDelete}
                  disabled={isLoading}
                >
                  キャンセル
                </button>
                <button
                  className="btn btn-primary button-medium"
                  onClick={confirmDelete}
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? '#ccc' : '#dc3545',
                    borderColor: isLoading ? '#ccc' : '#dc3545'
                  }}
                >
                  {isLoading ? '削除中...' : '削除する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgencyInsuranceTab;